// src/clients/cart/services/cart-service-hybrid.ts
// ✅ Versión consolidada con lógica correcta de ofertas por cantidad

import {
  cartApiService,
  type AgregarProductoRequest,
  type ProductoCarritoAPI,
} from "./cart-api-service";

import type {
  CarritoData,
  ProductoCarrito,
  ComboCarritoItem,
  AgregarProductoOptions,
  AgregarComboOptions,
  TipoSolicitanteAPI,
  UiConfirmArgs,
  UiConfirmFn,
  BulkAgregarItem,
} from "./hybrid/cart-types";

import { offerValidationService } from "./offer-validation-service";

import {
  toNum,
  r2,
  normalizeId,
  isLogout,
  mapTipoSolicitante,
} from "./hybrid/cart-utils";

import {
  esTransferGenerico,
  unificarProductosTransfer,
} from "./hybrid/transfer-helpers";

import {
  convertirDeAPI,
  mergeByLogicalKeySum,
  logicalKey,
  finalizeTransfers,
} from "./hybrid/api-adapter";

/* ============================================================
 * Tipos auxiliares
 * ============================================================ */

export type PagedResult<T> = {
  items: T[];
  total: number;
  totalPages: number;
  page: number;
  start: number;
  end: number;
};

// Guardamos solo productos + cache combos
interface LocalStorageData {
  productos: ProductoCarrito[];
  comboCache: Map<
    string,
    { precio_unitario: number; precio_total: number; timestamp: number }
  >;
}

/* ============================================================
 * Helpers de storage
 * ============================================================ */

function guardarLocal(
  productos: ProductoCarrito[],
  comboCache: Map<string, any>
) {
  // ✅ Persistence disabled by user request
}

function cargarLocal(): LocalStorageData {
  return { productos: [], comboCache: new Map() };
}

function limpiarBasuraLocalStorage() {
  const basuraKeys = [
    "ofertas_bloqueadas",
    "ofertas_guardadas",
    "ofertas_restauradas",
    "oferta_id_from_carousel",
    "oferta_seleccionada",
    "ofertas_online_cache_v2",
    "pedidos_locales",
    "negos",
    "drosi-open",
  ];

  basuraKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch { }
  });
}

/* ============================================================
 * Servicio principal
 * ============================================================ */

export class CartServiceHybrid {
  private productos: ProductoCarrito[] = [];
  private listeners: Array<(c: CarritoData) => void> = [];

  private syncInProgress = false;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSyncTime = 0;
  private readonly SYNC_THROTTLE = 800 as const;
  private rawCarritoData: any = null;

  private uiConfirm?: UiConfirmFn;
  private modalLockUntil = 0;

  constructor() {
    limpiarBasuraLocalStorage();

    const snap = cargarLocal();
    this.productos = snap.productos;

    this.setupAuthListeners();
    this.checkSessionOnBoot();
  }

  /* ===================== Boot / Auth ===================== */

  private checkSessionOnBoot(): void {
    try {
      const hasToken =
        !!localStorage.getItem("auth_token") ||
        sessionStorage.getItem("admin_authenticated") === "true";

      if (hasToken) {
        setTimeout(() => {
          this.sincronizarDesdeServidor();
        }, 100);  // Reduced from 300ms for faster cart loading
      } else {
        this.notifyListeners();
      }
    } catch {
      this.notifyListeners();
    }
  }

  private setupAuthListeners(): void {
    window.addEventListener("auth:session-ready", () =>
      setTimeout(() => {
        // Solo sincronizar si el boot sync no lo hizo recientemente
        if (Date.now() - this.lastSyncTime > 2000) {
          this.sincronizar().catch(() => { });
        }
      }, 100)
    );

    const clearLocal = () => {
      this.productos = [];
      limpiarBasuraLocalStorage();
      this.emit();
    };

    window.addEventListener("auth:session-cleared", clearLocal);
    window.addEventListener("app:logout", clearLocal);
  }

  /* ===================== UI Confirm ===================== */

  public setUiConfirm(fn?: UiConfirmFn) {
    this.uiConfirm = fn;
  }

  private async fallbackConfirm(args: UiConfirmArgs): Promise<boolean> {
    const { default: Swal } = await import("sweetalert2");
    const r = await Swal.fire({
      title: args.title,
      html: args.html,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
      customClass: { popup: "ux-swal" },
    });
    return r.isConfirmed;
  }

  private async askOnce(args: UiConfirmArgs): Promise<boolean> {
    const now = Date.now();
    if (now < this.modalLockUntil) return true;
    this.modalLockUntil = now + 1200;

    const ok = this.uiConfirm
      ? await this.uiConfirm(args)
      : await this.fallbackConfirm(args);

    this.modalLockUntil = 0;
    return ok;
  }

  /* ===================== API pública básica ===================== */

  subscribe(cb: (c: CarritoData) => void) {
    this.listeners.push(cb);
    cb(this.getCarritoData());
    return () => {
      this.listeners = this.listeners.filter((f) => f !== cb);
    };
  }

  getCartItems() {
    return this.getCantidadTotal();
  }

  getCartTotal() {
    return this.getCarritoData().total;
  }

  getCantidadTotal() {
    return this.productos.reduce((s, p) => s + p.cantidad, 0);
  }

  setCarritoData(data: CarritoData) {
    this.productos = [...data.productos];
    this.emit();
  }

  getCarritoData(): CarritoData {
    let subtotalLista = 0;
    let totalVigente = 0;
    let cantidadItems = 0;

    for (const p of this.productos) {
      const qty = Math.max(1, Number(p.cantidad) || 1);
      const lista = this.listaUnit(p);
      subtotalLista += r2(lista * qty);
      totalVigente += r2(p.precio_total);
      cantidadItems += qty;
    }

    const descuentos = r2(subtotalLista - totalVigente);
    const total = Math.max(0, r2(totalVigente));

    const combos = this.getCombos();
    const indiv = this.getProductosIndividuales();

    const productosClonados = this.productos.map((p) => ({ ...p }));

    return {
      productos: productosClonados,
      subtotal: subtotalLista,
      descuentos,
      total,
      cantidad_items: cantidadItems,
      estadisticas_combos: {
        total_combos: combos.length,
        total_individuales: indiv.length,
        valor_combos: combos.reduce((s, c) => s + c.precio_total, 0),
        valor_individuales: indiv.reduce((s, x) => s + x.precio_total, 0),
      },
    };
  }

  getCombos(): ComboCarritoItem[] {
    return this.productos.filter((p) => this.esCombo(p)) as ComboCarritoItem[];
  }

  getProductosIndividuales() {
    return this.productos.filter((p) => !this.esCombo(p));
  }

  getTransfers() {
    return this.productos.filter((p) => this.esTransfer(p));
  }

  tieneCombo(id: string | number) {
    return this.productos.some(
      (p) => this.esCombo(p) && p.combo_id === id
    );
  }

  getCantidadCombo(id: string | number) {
    return (
      (this.productos.find(
        (p) => this.esCombo(p) && p.combo_id === id
      ) as ComboCarritoItem | undefined)?.cantidad || 0
    );
  }

  tieneTransfer(cod: string) {
    return this.productos.some(
      (p) => this.esTransfer(p) && p.cod_drog === cod
    );
  }

  getCantidadTransfer(cod: string) {
    return (
      this.productos.find(
        (p) => this.esTransfer(p) && p.cod_drog === cod
      )?.cantidad || 0
    );
  }

  getVisibleForCart(): ProductoCarrito[] {
    return (this.productos || []).filter((p) => {
      const ts = String((p as any).tipo_solicitante || "").toUpperCase();
      const esComboOferta =
        this.esCombo(p) &&
        !!p.combo_id &&
        (ts === "C" || ts === "L" || ts === "O" || ts === "D");

      if (!esComboOferta) return true;
      return ts === "O" || ts === "D";
    });
  }

  getVisibleOffersCL(): ProductoCarrito[] {
    return (this.productos || []).filter((p) => {
      const ts = String((p as any).tipo_solicitante || "").toUpperCase();
      return this.esCombo(p) && !!p.combo_id && (ts === "C" || ts === "L");
    });
  }

  getPage<T>(items: T[], page = 1, pageSize = 5): PagedResult<T> {
    const total = items.length;
    const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
    const clamped = Math.min(Math.max(1, page), totalPages);
    const startIndex = (clamped - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, total);

    return {
      items: items.slice(startIndex, endIndex),
      total,
      totalPages,
      page: clamped,
      start: total === 0 ? 0 : startIndex + 1,
      end: endIndex,
    };
  }

  getCartPage(page = 1, pageSize = 5): PagedResult<ProductoCarrito> {
    return this.getPage(this.getVisibleForCart(), page, pageSize);
  }

  /* ===================== Helpers de precio/oferta ===================== */

  private listaUnit(p: ProductoCarrito): number {
    if (this.esCombo(p)) return p.precio_unitario;

    const EPS = 0.005;
    const listaExpl = toNum((p as any).precio_lista_unitario, 0);
    if (listaExpl > 0 && listaExpl >= p.precio_unitario - EPS) return r2(listaExpl);

    const prFarmacia = toNum((p as any).pr_farmacia, 0);
    if (prFarmacia > 0 && prFarmacia >= p.precio_unitario + EPS) return r2(prFarmacia);

    const pct = toNum((p as any).desc_porc ?? (p as any).descuento ?? 0, 0);
    if (pct > 0 && p.precio_unitario > 0) {
      const rec = p.precio_unitario / (1 - pct / 100);
      if (rec >= p.precio_unitario + EPS) return r2(rec);
    }

    const prPublico = toNum((p as any).pr_publico, 0);
    if (prPublico > 0 && prPublico >= p.precio_unitario + EPS) return r2(prPublico);

    const precioCrudo = toNum((p as any).precio, 0);
    if (precioCrudo > 0 && precioCrudo >= p.precio_unitario + EPS) return r2(precioCrudo);

    return r2(p.precio_unitario);
  }

  /**
   * Convierte una línea con oferta aplicada a producto común.
   * Marca __oferta_perdida para que el estado persista mientras esté bajo el mínimo.
   */
  private convertToIndividualLocal(p: any, qty: number, motivo: string) {
    const EPS = 0.005;

    const listaExpl = Number(p?.precio_lista_unitario || 0);
    const prFarmacia = Number(p?.pr_farmacia || 0);
    const pct = Number(p?.desc_porc ?? p?.descuento ?? 0);
    const prPublico = Number(p?.pr_publico || 0);
    const precioCrudo = Number(p?.precio || 0);

    let lista = 0;
    if (listaExpl > 0 && listaExpl >= p.precio_unitario - EPS) lista = listaExpl;
    else if (prFarmacia > 0 && prFarmacia >= p.precio_unitario + EPS)
      lista = prFarmacia;
    else if (pct > 0 && p.precio_unitario > 0) {
      const rec = p.precio_unitario / (1 - pct / 100);
      if (rec >= p.precio_unitario + EPS) lista = rec;
    } else if (prPublico > 0 && prPublico >= p.precio_unitario + EPS)
      lista = prPublico;
    else if (precioCrudo > 0 && precioCrudo >= p.precio_unitario + EPS)
      lista = precioCrudo;
    else lista = p.precio_unitario;

    p.oferta = false;
    p.promocional = false;
    p.tipo_solicitante = "C";
    p.desc_porc = 0;
    p.descuento = 0;
    p.precio_oferta = 0;
    p.tiene_oferta_cantidad = false;
    p.cumple_oferta = false;

    p.precio_unitario = r2(lista);
    p.precio_total = r2(lista * qty);

    // 🔴 clave: recordar que el usuario eligió perder la promo
    p.__oferta_perdida = true;

    return p as ProductoCarrito;
  }

  /* ===================== Validación cambio oferta ===================== */

  private async validarCambioOferta(
    producto: ProductoCarrito,
    cantidadNueva: number
  ): Promise<{
    permitir: boolean;
    convertirAIndividual: boolean;
    aplicarPromoEnServer: boolean;
  }> {
    const cantidadActual = producto.cantidad;

    try {
      const validacion =
        await offerValidationService.validarCambioCantidadConCantidadActual(
          producto,
          cantidadActual,
          cantidadNueva
        );

      // 🔴 Va a perder la promo
      if (validacion.pierde_promocion && validacion.mensaje_confirmacion) {
        // ✅ SKIP ASK: Ya se preguntó en el componente UI inmediatamente
        return {
          permitir: true,
          convertirAIndividual: true,
          aplicarPromoEnServer: false,
        };
      }

      // 🟢 Va a ganar la promo
      if (validacion.gana_promocion && validacion.mensaje_confirmacion) {
        // ✅ SKIP ASK: Ya se preguntó en el componente UI inmediatamente
        return {
          permitir: true,
          convertirAIndividual: false,
          aplicarPromoEnServer: true,
        };
      }

      // No hay impacto en promo
      return {
        permitir: true,
        convertirAIndividual: false,
        aplicarPromoEnServer: false,
      };
    } catch (error) {
      console.warn("⚠️ Error validando oferta:", error);
      // Si falla la validación no bloqueamos el cambio
      return {
        permitir: true,
        convertirAIndividual: false,
        aplicarPromoEnServer: false,
      };
    }
  }

  /* ===================== Agregar producto / combo ===================== */

  async agregarProducto(
    prod: Omit<ProductoCarrito, "precio_total" | "id">,
    opts: AgregarProductoOptions = {}
  ) {
    const {
      sincrono = true,
      tipo_solicitante = "C",
      forzar_sin_combo = false,
      cantidad,
      omitir_sync = false,
    } = opts;

    const cantidadFinal = cantidad ?? prod.cantidad;

    // 🔍 Buscar producto existente considerando tipo_solicitante
    const existe = this.productos.find(
      (p) => p.cod_drog === prod.cod_drog &&
        !this.esCombo(p) &&
        (p as any).tipo_solicitante === tipo_solicitante
    );



    // ⚡ Si existe con el MISMO tipo_solicitante, actualizar cantidad directamente
    if (existe) {
      return this.actualizarCantidad(
        (existe as any).id,
        (existe as any).cantidad + cantidadFinal,
        { tipo_solicitante }
      );
    }

    // ⚠️ Si existe con DIFERENTE tipo_solicitante, NO actualizar, sino agregar vía API
    // para que el backend maneje la lógica de ofertas correctamente

    if (!isLogout() && sincrono) {
      try {
        const req: AgregarProductoRequest = {
          cod_drog: prod.cod_drog,
          cantidad: String(cantidadFinal),
          tipo_solicitante: mapTipoSolicitante(tipo_solicitante),
          forzar_sin_combo,
        };

        // ⚡ Agregar al API de forma optimista (sin esperar sincronización)
        const res = await cartApiService.agregarProducto(req);
        if (res.success) {
          // ✅ CRÍTICO: Solo usar datos LIMPIOS del API si fueron realmente limpiados
          // (es decir, cuando tipo_solicitante="C" Y cumple_oferta=false)
          const cumpleOferta = res.data?.descuentos?.cumple_oferta === true;
          const debeUsarDatosLimpios = req.tipo_solicitante === "C" && !cumpleOferta;

          const productoDatos = debeUsarDatosLimpios ? (res.data?.pedido || res.data) : null;

          // ⚡ Agregar localmente: solo sobrescribir si limpiamos el descuento
          const productoLocal = {
            ...prod,
            ...(productoDatos || {}),  // ← Solo sobrescribe si realmente limpiamos
            cantidad: cantidadFinal
          };

          // ✅ Marcar producto que fue agregado con "Mantener" para que NO muestre oferta durante sincronización
          // ✨ FIX: Si el usuario eligió "C" (Mantener), SIEMPRE marcamos __mantener, 
          // independientemente de si cumple o no oferta en este momento.
          if (req.tipo_solicitante === "C") {
            (productoLocal as any).__mantener = true;
          }

          this.agregarProductoLocal(productoLocal);

          // Sincronizar en background solo si no estamos en modo importación masiva
          if (!omitir_sync) {
            this.sincronizarDesdeServidor().catch(() => { });
          }
          return { success: true, message: res.message };
        }

        this.agregarProductoLocal({ ...prod, cantidad: cantidadFinal });
        return { success: false, message: res.message };
      } catch {
        this.agregarProductoLocal({ ...prod, cantidad: cantidadFinal });
        return {
          success: false,
          message: "Error de conexión, agregado localmente",
        };
      }
    }

    this.agregarProductoLocal({ ...prod, cantidad: cantidadFinal });
    this.programarSincronizacion();
    return { success: true, message: "Producto agregado localmente" };
  }

  async agregarCombo(
    comboData: {
      cod_combo: string | number;
      descripcion: string;
      cantidad_combo: number;
      precio_total: number;
      productos_incluidos?: number;
      productos_seleccionados?: number;
      combo_detalle?: any;
    },
    options: AgregarComboOptions = {}
  ) {
    const { sincrono = true } = options;

    const qty = Math.max(1, Number(comboData.cantidad_combo) || 1);
    const unit = qty > 0 ? comboData.precio_total / qty : 0;

    const selecciones: Array<{ codigo_sel: string; codigo_drog: string }> = [];
    if (Array.isArray(comboData?.combo_detalle?.productos)) {
      for (const p of comboData.combo_detalle.productos) {
        if (p?.tipo !== "selectivo") continue;
        const codigo_sel = String(
          p.codigo_sel ?? p.cod_sel ?? p.codgrupo ?? ""
        ).trim();
        const codigo_drog = String(
          p.codigo_drog ?? p.cod_drog ?? ""
        ).trim();
        if (codigo_sel && codigo_drog) {
          selecciones.push({ codigo_sel, codigo_drog });
        }
      }
    }

    const comboLocal: ComboCarritoItem = {
      id: `combo_${comboData.cod_combo}_${Date.now()}`,
      cod_drog: `COMBO_${comboData.cod_combo}`,
      descripcion: comboData.descripcion,
      precio_unitario: r2(unit),
      cantidad: qty,
      precio_total: r2(comboData.precio_total),
      es_combo: true,
      combo_id: comboData.cod_combo,
      combo_detalle: [],
      productos_incluidos: comboData.productos_incluidos || 0,
      productos_seleccionados: comboData.productos_seleccionados || 0,
      combo_cantidad_base: qty,
      tipo_solicitante: "C",
    };

    // ✅ Sync local desactivado (cache removido)

    if (!sincrono || isLogout()) {
      this.agregarComboLocal(comboLocal);
      this.programarSincronizacion();
      return { success: true, message: "Combo agregado localmente" };
    }

    for (let i = 0; i < qty; i++) {
      const resp = await cartApiService.agregarCombo({
        cod_combo: comboData.cod_combo,
        tipo_solicitante: "C",
        selecciones,
      });
      if (!resp?.success) {
        return {
          success: false,
          message: resp?.message || "Error agregando combo",
        };
      }
    }

    const sync = await this.sincronizarDesdeServidor();
    return sync.success
      ? { success: true, message: "Combo agregado y sincronizado" }
      : {
        success: false,
        message: "Combo agregado (error de sincronización)",
      };
  }

  /* ===================== Actualizar cantidad ===================== */

  async actualizarCantidad(
    idOrCod: string | number,
    nuevaCantidad: number,
    options?: { tipo_solicitante?: string }
  ) {
    const needle = normalizeId(idOrCod);

    let producto =
      this.productos.find((p) => normalizeId((p as any).id) === needle) ||
      this.productos.find((p) => normalizeId(p.cod_drog) === needle);

    if (!producto) {
      return { success: false, message: "Producto no encontrado" };
    }

    if (nuevaCantidad <= 0) {
      return this.eliminarProducto((producto as any).id);
    }

    const cantidadAntes = Number((producto as any).cantidad) || 1;

    // Transfer: respetar mínimo
    if (this.esTransfer(producto)) {
      const txt = String(producto.observacion || "").toLowerCase();
      const m1 = txt.match(/min[:=]\s*(\d+)/);
      const m2 = txt.match(/mínimo\s+(\d+)/);
      const min = Math.max(1, Number(m1?.[1] ?? m2?.[1] ?? 1));
      if (nuevaCantidad > cantidadAntes && nuevaCantidad < min) {
        return {
          success: false,
          message: `Transfer con mínimo de ${min} unidades`,
        };
      }
    }

    const snapshot = { ...(producto as any) };
    const qty = Math.max(1, Math.floor(Number(nuevaCantidad)) || 1);

    // ✨ FIX: Al cambiar cantidad, limpiamos el flag __mantener para permitir que
    // el producto pueda transformar a oferta si cumple las condiciones
    delete (producto as any).__mantener;

    (producto as any).cantidad = qty;

    // ✅ CORREGIDO: Usar precio_si (sin IVA) como base para el cálculo del total
    // Si no existe precio_si, usar precio_unitario (legacy)
    const baseParaTotal = (producto as any).precio_si > 0
      ? (producto as any).precio_si
      : (producto as any).precio_unitario;

    (producto as any).precio_total = r2(baseParaTotal * qty);
    this.emit();

    if (isLogout()) {
      return { success: true, message: "Actualizado localmente" };
    }

    try {
      // ✅ ESTRATEGIA DE UPGRADE A OFERTA (Delete + Add)
      // Si el usuario pide explícitamente pasar a oferta ('O'), forzamos Delete+Add
      if (options?.tipo_solicitante === 'O') {


        await cartApiService.eliminarProducto((producto as any).id).catch(() => { });

        const resAdd = await cartApiService.agregarProducto({
          cod_drog: producto.cod_drog,
          cantidad: qty,
          tipo_solicitante: 'O',
          forzar_sin_combo: false
        });

        if (resAdd.success) {
          const ag = resAdd.data;
          const rawItem = ag?.pedido || ag;

          if (rawItem) {
            const newItemLocal = cartApiService.convertirAProductoLocal(rawItem);

            // ✅ Grace period desactivado (cache removido)

            this.productos = this.productos.map(p =>
              (p as any).id === (producto as any).id ? newItemLocal : p
            );
            this.emit();
          }

          this.programarSincronizacion(100);
          return { success: true, message: "Oferta aplicada exitosamente" };
        } else {
          throw new Error(resAdd.message || "Error al aplicar oferta");
        }
      }

      const res = await cartApiService.actualizarCantidad(
        (producto as any).id,
        qty,
        options?.tipo_solicitante
      );

      if (res.success) {
        this.programarSincronizacion(450);
        return {
          success: true,
          message: res.message || "Cantidad actualizada",
        };
      }

      Object.assign(producto, snapshot);
      this.emit();
      return {
        success: false,
        message:
          (res.message || "Error actualizando") + " (cambios revertidos)",
      };
    } catch {
      Object.assign(producto, snapshot);
      this.emit();
      return {
        success: false,
        message: "Error de conexión (cambios revertidos)",
      };
    }
  }

  /**
   * Usuario acepta perder la promo:
   * - Local: queda como producto común (badge gris, sin descuento, __oferta_perdida = true)
   * - Server: solo actualizamos la cantidad; NO usamos forzar_sin_combo.
   * - Sync: mientras qty < mínimo y exista __oferta_perdida, forzamos producto común aunque llegue mezclado.
   * - Si luego sube qty y acepta ganar promo, limpiamos __oferta_perdida y el server vuelve a aplicar oferta.
   */
  async convertirOfertaAIndividual(
    idOrCod: string | number,
    nuevaCantidad: number
  ) {
    const p =
      this.productos.find((x) => String((x as any).id) === String(idOrCod)) ||
      this.productos.find((x) => x.cod_drog === String(idOrCod));

    if (!p) {
      return { success: false, message: "Producto no encontrado" };
    }
    if (this.esCombo(p)) {
      return { success: false, message: "Es combo; no aplica" };
    }

    const snapshot = { ...(p as any) };
    const qty = Math.max(1, Number(nuevaCantidad) || snapshot.cantidad || 1);

    // 1) Local: convertir inmediatamente
    this.convertToIndividualLocal(p, qty, "Cantidad menor al mínimo requerido");
    this.emit();

    if (isLogout()) {
      return { success: true, message: "Conversión local" };
    }

    try {
      // 2) Backend: solo actualizamos cantidad; backend pasará a precio normal
      const res = await cartApiService.actualizarCantidad(
        (p as any).id,
        qty
      );

      if (!res.success) {
        Object.assign(p, snapshot);
        this.emit();
        return {
          success: false,
          message: res.message || "No se pudo convertir",
        };
      }

      this.programarSincronizacion(450);
      return {
        success: true,
        message: "Producto convertido a individual",
      };
    } catch {
      Object.assign(p, snapshot);
      this.emit();
      return { success: false, message: "Error de conexión" };
    }
  }

  /* ===================== Eliminar ===================== */

  async eliminarProducto(idOrCod: string | number) {
    const key = String(idOrCod);

    const p =
      this.productos.find((x) => String((x as any).id) === key) ||
      this.productos.find((x) => x.cod_drog === key) ||
      this.productos.find(
        (x) =>
          this.esCombo(x) &&
          String((x as any).combo_id) === key
      );

    if (!p) {
      return { success: false, message: "Producto no encontrado" };
    }

    if (this.esCombo(p) && (p as any).combo_id) {
      const snap = [...this.productos];

      this.productos = this.productos.filter(
        (x) =>
          !this.esCombo(x) ||
          String((x as any).combo_id) !== String((p as any).combo_id!)
      );
      // Limpieza local de combos (cache removido)
      this.emit();

      if (isLogout()) {
        return { success: true, message: "Eliminado localmente" };
      }

      try {
        const r = await cartApiService.eliminarCombo((p as any).combo_id!);
        if (!r?.success) {
          this.productos = snap;
          this.emit();
          return {
            success: false,
            message: r?.message || "No se pudo eliminar",
          };
        }
        return { success: true, message: r.message };
      } catch {
        this.productos = snap;
        this.emit();
        return {
          success: false,
          message: "Error de conexión",
        };
      }
    }

    return this.eliminarPorCodDrog(p.cod_drog);
  }

  async eliminarPorCodDrog(cod: string) {
    const items = this.productos.filter(
      (p) => !this.esCombo(p) && p.cod_drog === cod
    );
    if (items.length === 0) return { success: true };

    const snap = [...this.productos];

    this.productos = this.productos.filter(
      (p) => p.cod_drog !== cod || this.esCombo(p)
    );
    this.emit();

    if (isLogout()) {
      return { success: true, message: "Eliminado localmente" };
    }

    try {
      const resp = await cartApiService.eliminarPorCodDrog(cod);

      if (!resp.success) {
        this.productos = snap;
        this.emit();
        return {
          success: false,
          message: resp.message || "Error eliminando",
        };
      }

      return {
        success: true,
        message: resp.message || "Producto eliminado",
      };
    } catch {
      this.productos = snap;
      this.emit();
      return {
        success: false,
        message: "Error de conexión",
      };
    }
  }

  async vaciarCarrito() {
    try {
      const snap = [...this.productos];
      this.productos = [];
      // Clear (cache removido)
      this.emit();

      if (isLogout()) {
        this.persist();
        return { success: true, message: "Vaciado local" };
      }

      await Promise.allSettled(
        snap.map((p) =>
          this.esCombo(p) && (p as any).combo_id
            ? cartApiService.eliminarCombo((p as any).combo_id)
            : cartApiService.eliminarProducto((p as any).id)
        )
      );

      await this.sincronizarDesdeServidor();
      return { success: true, message: "Carrito vaciado" };
    } catch {
      this.productos = [];
      // Clear (cache removido)
      this.emit();
      return {
        success: false,
        message: "Error vaciando",
      };
    }
  }

  /* ===================== Sync con servidor ===================== */

  async sincronizar() {
    const now = Date.now();
    if (this.syncInProgress) {
      return { success: false, message: "Sync en progreso" };
    }
    if (now - this.lastSyncTime < this.SYNC_THROTTLE) {
      this.programarSincronizacion(300);
      return { success: false, message: "Throttle" };
    }
    return this.sincronizarDesdeServidor();
  }

  async sincronizarDesdeServidor() {
    if (this.syncInProgress) {
      return { success: false, message: "Sync en curso" };
    }
    if (isLogout()) {
      return { success: false, message: "Logout en progreso" };
    }

    this.syncInProgress = true;
    this.lastSyncTime = Date.now();

    try {
      const srv = await cartApiService.obtenerCarrito();
      if (!srv.success) {
        return { success: false, message: srv.message || "Error" };
      }

      const d = srv.data || {};
      this.rawCarritoData = d; // guardar para combo-cart-service sin GET extra
      const base = [
        ...(Array.isArray(d.productos) ? d.productos : []),
        ...(Array.isArray(d.ofertas_online) ? d.ofertas_online : []),
      ] as ProductoCarritoAPI[];

      let serverItems = mergeByLogicalKeySum(base.map(convertirDeAPI));

      // Reaplicar cache combos (cache removido)

      // Respetar flags de la versión local
      const prevMap = new Map<string, any>(
        (this.productos || []).map((p) => [logicalKey(p), p as any])
      );

      // ✨ FIX: Mapa secundario por ID para encontrar items cuando cambia la key lógica
      // (ej: de "C" a "O" o viceversa)
      const prevMapById = new Map<string, any>();
      (this.productos || []).forEach(p => {
        if (p.id) prevMapById.set(String(p.id), p as any);
      });

      for (const it of serverItems as any[]) {
        if (this.esCombo(it)) continue;

        const key = logicalKey(it);
        // Intentar buscar por key lógica, y si no, por ID
        let prev = prevMap.get(key);

        if (!prev && it.id) {
          prev = prevMapById.get(String(it.id));
        }


        // ✅ PRESERVAR unidades_por_modulo del producto local
        if (prev && prev.unidades_por_modulo) {
          console.log(`✅ [Preserve-OK] ${it.cod_drog}: Preserving unidades_por_modulo=${prev.unidades_por_modulo}`);
          (it as any).unidades_por_modulo = prev.unidades_por_modulo;
        }

        // 🚨 FIX: Si el servidor devuelve explícitamente OFERTA ('O'), ignoramos __mantener local
        const serverEsOferta = (it as any).tipo_solicitante === 'O' || (it as any).oferta === true || (it as any).oferta === 'S';

        // ✅ Grace period desactivado
        const inGracePeriod = false;

        if (inGracePeriod && !serverEsOferta) {
          (it as any).tipo_solicitante = "O";
          (it as any).oferta = true;
          (it as any).promocional = true;

          // Copiar precios del snapshot local si existe
          const localSnapshot = (this.productos || []).find(p => p.cod_drog === it.cod_drog && !this.esCombo(p));
          if (localSnapshot) {
            if ((localSnapshot as any).precio_oferta) (it as any).precio_oferta = (localSnapshot as any).precio_oferta;
            if ((localSnapshot as any).descuento) (it as any).descuento = (localSnapshot as any).descuento;
            (it as any).precio_unitario = (localSnapshot as any).precio_unitario;
            (it as any).precio_total = (localSnapshot as any).precio_total;
            (it as any).desc_porc = (localSnapshot as any).desc_porc;
          }
        }

        // ✅ Respetar __mantener: producto agregado con "Mantener" no debe mostrar oferta
        if (prev && prev.__mantener && !serverEsOferta && !inGracePeriod) {
          // Limpiar descuentos para que no muestre oferta
          (it as any).desc_porc = 0;
          (it as any).descuento = 0;
          (it as any).precio_oferta = 0;
          (it as any).tipo_solicitante = "C"; // Forzar tipo C
          (it as any).__mantener = true;

          // ✨ Si forzamos "C", la logicalKey podría cambiar, pero como ya estamos
          // iterando sobre serverItems, esto solo afecta a cómo se guarda en this.productos
        }

        if (prev && prev.__oferta_perdida) {
          const qty = Number(it.cantidad) || Number(prev.cantidad) || 1;
          const min =
            Number(it.cantidad_minima_oferta) ||
            Number(prev.cantidad_minima_oferta) ||
            0;

          if (!min || qty < min) {
            // Sigue bajo el mínimo → forzamos producto común
            this.convertToIndividualLocal(
              it,
              qty,
              prev.observacion || "Cantidad menor al mínimo requerido"
            );
            it.__oferta_perdida = true;
          } else {
            // Ahora ya cumple el mínimo, pero todavía no tocó cantidad:
            // dejamos que el server decida (y en el próximo cambio se le ofrecerá ganar promo).
            delete it.__oferta_perdida;
          }
        }
      }

      const nextMap = new Map<string, ProductoCarrito>(
        serverItems.map((it) => [logicalKey(it), it as any])
      );

      // ✅ Preservar el orden local: si un item ya existe en this.productos, mantener su posición
      // Solo agregar items nuevos del servidor al final
      const reorderedItems: ProductoCarrito[] = [];

      // 1) Mantener items que ya existían en el mismo orden
      for (const localItem of this.productos || []) {
        const key = logicalKey(localItem);
        if (nextMap.has(key)) {
          const serverVersion = nextMap.get(key)!;
          reorderedItems.push(serverVersion);
          nextMap.delete(key); // Marcar como procesado
        }
      }

      // 2) Agregar items nuevos del servidor al final
      for (const item of nextMap.values()) {
        reorderedItems.push(item);
      }

      this.productos = finalizeTransfers(reorderedItems);
      this.emit();

      return { success: true, message: "Carrito sincronizado" };
    } catch (e) {
      console.error("❌ [sincronizarDesdeServidor]:", e);
      return { success: false, message: "Error de conexión" };
    } finally {
      this.syncInProgress = false;
    }
  }

  private programarSincronizacion(delay = 500) {  // Reduced from 1500ms for faster loading
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.sincronizarDesdeServidor();
    }, delay);
  }

  /* ===================== Helpers internos ===================== */

  private agregarProductoLocal(
    prod: Omit<ProductoCarrito, "precio_total" | "id">
  ) {
    // 🔍 Buscar producto existente con MISMO cod_drog, tipo_solicitante Y observacion
    // Esto permite que un producto revista y un producto oferta sean líneas separadas
    const es_revista = String(prod.observacion || "").toLowerCase().includes("revista");

    const ex = this.productos.find((p) => {
      if (p.cod_drog !== prod.cod_drog || this.esCombo(p)) return false;

      const obs_existente = String((p as any).observacion || "").toLowerCase();
      const es_revista_existente = obs_existente.includes("revista");
      const tipo_existente = (p as any).tipo_solicitante;
      const tipo_nuevo = (prod as any).tipo_solicitante;

      // ✅ Mismo tipo_solicitante
      if (tipo_existente !== tipo_nuevo) return false;

      // ✅ Si es revista vs no-revista: no mergear
      if (es_revista !== es_revista_existente) return false;

      return true;
    });

    if (ex) {
      (ex as any).cantidad += prod.cantidad;
      (ex as any).precio_total = r2(
        (ex as any).precio_unitario * (ex as any).cantidad
      );
    } else {
      this.productos.push({
        ...(prod as any),
        id: this.generarIdLocal(),
        precio_total: r2(prod.precio_unitario * prod.cantidad),
      } as any);
    }

    this.emit();
  }

  private agregarComboLocal(combo: ComboCarritoItem) {
    const ex = this.productos.find(
      (p) => this.esCombo(p) && (p as any).combo_id === combo.combo_id
    ) as ComboCarritoItem | undefined;

    if (ex) {
      (ex as any).cantidad += combo.cantidad;
      (ex as any).precio_total = r2(
        (ex as any).precio_unitario * (ex as any).cantidad
      );
      const baseEx = Number((ex as any).combo_cantidad_base ?? 1);
      const baseNew = Number((combo as any).combo_cantidad_base ?? 1);
      (ex as any).combo_cantidad_base = baseEx + baseNew;
    } else {
      this.productos.push(combo as any);
    }

    this.emit();
  }

  private generarIdLocal() {
    return `local_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 11)}`;
  }

  private esCombo(p: ProductoCarrito) {
    return (
      (p as any).es_combo === true ||
      (!!(p as any).combo_id &&
        normalizeId((p as any).combo_id) !== "0") ||
      p.cod_drog?.toUpperCase().startsWith("COMBO_") ||
      String((p as any).combo ?? "")
        .toUpperCase()
        .match(/^(S|SI|TRUE|1|COMBO)/) != null ||
      (p.descripcion || "").toLowerCase().includes("combo")
    );
  }

  private esTransfer(p: ProductoCarrito) {
    return esTransferGenerico(p);
  }

  getRawCarritoData() {
    return this.rawCarritoData;
  }

  private persist() {
    guardarLocal(this.productos, new Map());
  }

  private emit() {
    this.persist();
    this.notifyListeners();
    this.notifyGlobalEvent();
  }

  private notifyListeners() {
    const data = this.getCarritoData();
    for (const fn of this.listeners) {
      try {
        fn(data);
      } catch { }
    }
  }

  private notifyGlobalEvent() {
    const data = this.getCarritoData();
    try {
      window.dispatchEvent(
        new CustomEvent("cart-updated", { detail: data, bubbles: true })
      );
      window.dispatchEvent(
        new CustomEvent("cart-data-loaded", {
          detail: { carritoData: data },
          bubbles: true,
        })
      );
    } catch { }
  }
}

/* ============================================================
 * Singleton + util duplicados transfers
 * ============================================================ */

export const cartServiceHybrid = new CartServiceHybrid();

export function detectarProductosDuplicados(
  carritoData: CarritoData
): CarritoData {
  const productosFinales = unificarProductosTransfer(
    carritoData.productos
  );

  const subtotal = productosFinales.reduce(
    (s, p) => s + (p as any).precio_total,
    0
  );

  const calcDesc = (p: ProductoCarrito) => {
    const pct = toNum(
      ((p as any).desc_porc ?? (p as any).descuento ?? 0),
      0
    );
    if (
      pct > 0 &&
      (p as any).precio_unitario > 0 &&
      (p as any).cantidad > 0
    ) {
      const lista =
        (p as any).precio_unitario / (1 - pct / 100);
      return (
        (lista - (p as any).precio_unitario) *
        (p as any).cantidad
      );
    }
    const oferta = toNum(
      ((p as any).precio_oferta ?? 0),
      0
    );
    return oferta > 0 &&
      oferta < (p as any).precio_unitario
      ? ((p as any).precio_unitario - oferta) *
      (p as any).cantidad
      : 0;
  };

  const descuentos = productosFinales.reduce(
    (s, p) => s + Math.max(0, calcDesc(p)),
    0
  );

  const total = Math.max(0, subtotal - descuentos);
  const cantidad_items = productosFinales.reduce(
    (s, p) => s + (p as any).cantidad,
    0
  );

  return {
    productos: productosFinales,
    subtotal,
    descuentos,
    total,
    cantidad_items,
    estadisticas_combos:
      carritoData.estadisticas_combos,
  };
}

/* Debug en dev */
if (
  typeof window !== "undefined" &&
  (process.env.NODE_ENV === "development" as any)
) {
  (window as any).cartDebug = () => {
    const data = cartServiceHybrid.getCarritoData();
    console.table(data.productos);
  };
}

export type {
  CarritoData,
  ProductoCarrito,
  ComboCarritoItem,
  AgregarProductoOptions,
  AgregarComboOptions,
  TipoSolicitanteAPI,
  UiConfirmArgs,
  UiConfirmFn,
  BulkAgregarItem,
} from "./hybrid/cart-types";

